import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { getCurrentCDSToken } from "./Auth";
import { useUser } from "./User";
import { AudioContext } from "./AudioPlayer";
import { useTheme } from "./ThemeContext"; // Import the theme hook
import config from "./config";
import AudiobookPromptSelector from "./AudiobookPromptSelector";

// Get styles based on current theme
const getItemStyles = (theme) => {
   const isLight = theme === "light";
   return {
      container: (isCurrentlyPlaying) => ({
         marginBottom: "1rem",
         display: "flex",
         alignItems: "center",
         justifyContent: "space-between",
         padding: "0.5rem",
         borderBottom: isCurrentlyPlaying
            ? `1px solid ${
                 isLight ? "rgba(75, 85, 99, 0.1)" : "rgba(75, 85, 99, 0.2)"
              }`
            : `1px solid ${isLight ? "var(--border-color)" : "#333"}`,
         position: "relative",
         backgroundColor: isCurrentlyPlaying
            ? isLight
               ? "rgba(75, 85, 99, 0.05)"
               : "rgba(75, 85, 99, 0.1)"
            : "transparent",
         borderRadius: isCurrentlyPlaying ? "4px" : "0",
         boxShadow: isCurrentlyPlaying
            ? `0 0 0 1px ${
                 isLight ? "rgba(75, 85, 99, 0.05)" : "rgba(75, 85, 99, 0.1)"
              }`
            : "none",
         transition: "all 0.2s ease",
      }),
      generatingIndicator: {
         color: isLight ? "#9ca3af" : "#d1d5db",
         fontSize: "0.7rem",
         fontStyle: "italic",
         margin: "2px 0 0 0",
      },
      playButton: (isCurrentlyPlaying, isGenerating = false) => {
         const baseStyle = {
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            backgroundColor: isLight ? "var(--bg-tertiary)" : "#111",
            border: isCurrentlyPlaying
               ? `2px solid ${isLight ? "var(--accent-primary)" : "#10b981"}`
               : `2px solid ${isLight ? "var(--border-color)" : "#333"}`,
            color: isLight ? "var(--text-primary)" : "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: "1rem",
            cursor: "pointer",
         };

         if (isGenerating) {
            return {
               ...baseStyle,
               opacity: 0.7,
               cursor: "default",
               backgroundColor: isLight ? "#f3f4f6" : "#2d3748",
               borderColor: isLight ? "#d1d5db" : "#4b5563",
               color: isLight ? "#9ca3af" : "#6b7280",
            };
         }

         return baseStyle;
      },
      imagePlaceholder: {
         width: "56px",
         height: "56px",
         backgroundColor: isLight ? "var(--bg-tertiary)" : "#222",
         borderRadius: "4px",
         marginRight: "1rem",
      },
      bookImage: {
         width: "56px",
         height: "56px",
         objectFit: "cover",
         borderRadius: "4px",
         marginRight: "1rem",
      },
      titleContainer: {
         flex: 1,
      },
      bookTitle: (isCurrentlyPlaying) => ({
         margin: 0,
         color: "var(--text-primary)",
         fontSize: "1rem",
         fontWeight: isCurrentlyPlaying ? "500" : "normal",
      }),
      chapterInfo: {
         margin: "4px 0 2px 0",
         color: isLight ? "var(--text-muted)" : "#999",
         fontSize: "0.875rem",
      },
      lastPlayed: {
         margin: 0,
         color: isLight ? "var(--text-dimmed)" : "#777",
         fontSize: "0.75rem",
         fontStyle: "italic",
      },
      rightSide: {
         display: "flex",
         alignItems: "center",
         marginLeft: "12px",
      },
      publicBadge: {
         backgroundColor: isLight ? "var(--bg-tertiary)" : "#444",
         color: isLight ? "var(--text-muted)" : "#ddd",
         fontSize: "0.7rem",
         padding: "2px 8px",
         borderRadius: "10px",
         marginLeft: "8px",
      },
   };
};

const getPageStyles = (theme) => {
   const isLight = theme === "light";
   return {
      container: {
         maxWidth: "1200px",
         margin: "0 auto",
         padding: "3rem",
      },
      heading: {
         color: "var(--text-primary)",
         fontSize: "2rem",
         fontWeight: "700",
         marginBottom: "2rem",
         marginTop: "2rem",
      },
      subheading: {
         color: isLight ? "var(--text-secondary)" : "#ddd",
         fontSize: "1.2rem",
         fontWeight: "500",
         marginBottom: "1.5rem",
      },
      loadingContainer: {
         display: "flex",
         alignItems: "center",
         justifyContent: "center",
         padding: "3rem",
      },
      loadingText: {
         color: isLight ? "var(--text-muted)" : "#aaa",
         marginLeft: "1rem",
      },
      errorContainer: {
         padding: "1.5rem",
         backgroundColor: isLight
            ? "rgba(239, 68, 68, 0.05)"
            : "rgba(239, 68, 68, 0.1)",
         borderRadius: "0.5rem",
         marginBottom: "1.5rem",
      },
      errorText: {
         color: "#ef4444",
         marginBottom: "1rem",
      },
      errorButton: {
         backgroundColor: "#ef4444",
         color: "white",
         padding: "0.5rem 1rem",
         borderRadius: "0.375rem",
         border: "none",
         cursor: "pointer",
      },
      emptyStateContainer: {
         backgroundColor: isLight
            ? "var(--bg-secondary)"
            : "rgba(34, 34, 34, 0.98)",
         borderRadius: "0.5rem",
         border: `1px solid ${isLight ? "var(--border-color)" : "#333"}`,
         padding: "2rem",
         textAlign: "center",
         boxShadow: isLight
            ? "0 4px 8px rgba(0, 0, 0, 0.1)"
            : "0 4px 8px rgba(0, 0, 0, 0.3)",
         marginTop: "1rem",
      },
      emptyStateIcon: {
         width: "64px",
         height: "64px",
         borderRadius: "50%",
         backgroundColor: isLight
            ? "rgba(75, 85, 99, 0.05)"
            : "rgba(75, 85, 99, 0.1)",
         display: "flex",
         alignItems: "center",
         justifyContent: "center",
         margin: "0 auto 1.5rem auto",
         border: isLight
            ? "1px solid rgba(75, 85, 99, 0.1)"
            : "1px solid rgba(75, 85, 99, 0.2)",
      },
      emptyStateIconText: {
         color: isLight ? "var(--text-muted)" : "#999",
         fontSize: "2rem",
      },
      emptyStateHeading: {
         color: "var(--text-primary)",
         fontSize: "1.25rem",
         fontWeight: "500",
         marginBottom: "0.75rem",
      },
      emptyStateText: {
         color: isLight ? "var(--text-muted)" : "#999",
         fontSize: "0.9rem",
         marginBottom: "1.5rem",
         maxWidth: "400px",
         margin: "0 auto 1.5rem auto",
      },
      buttonContainer: {
         display: "flex",
         gap: "1rem",
         justifyContent: "center",
      },
      primaryButton: {
         backgroundColor: isLight ? "var(--accent-primary)" : "#10b981",
         color: "white",
         padding: "0.75rem 1.5rem",
         borderRadius: "0.375rem",
         border: "none",
         fontSize: "0.95rem",
         fontWeight: "500",
         cursor: "pointer",
         transition: "background-color 0.2s ease",
         boxShadow: isLight
            ? "0 2px 4px rgba(0, 0, 0, 0.1)"
            : "0 2px 4px rgba(0, 0, 0, 0.3)",
      },
      primaryButtonHover: {
         backgroundColor: isLight ? "var(--accent-primary-dark)" : "#0e9f6e",
      },
      secondaryButton: {
         backgroundColor: "transparent",
         color: isLight ? "var(--text-secondary)" : "#e0e0e0",
         padding: "0.75rem 1.5rem",
         borderRadius: "0.375rem",
         border: isLight
            ? "1px solid var(--border-color)"
            : "1px solid rgba(255, 255, 255, 0.2)",
         fontSize: "0.95rem",
         fontWeight: "500",
         cursor: "pointer",
         transition: "all 0.2s ease",
      },
      secondaryButtonHover: {
         backgroundColor: isLight
            ? "var(--bg-tertiary)"
            : "rgba(255, 255, 255, 0.05)",
         borderColor: isLight
            ? "var(--text-muted)"
            : "rgba(255, 255, 255, 0.3)",
      },
   };
};

const RecentlyPlayedItem = ({
   book,
   onListen,
   currentlyPlayingUuid,
   theme,
}) => {
   const styles = getItemStyles(theme);
   const [imageSrc, setImageSrc] = useState(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(false);
   const userId = localStorage.getItem("userId");
   const imageLoadedRef = useRef(false);

   // Get the audio context to check if this book is currently playing
   const audioContext = useContext(AudioContext);

   // Determine if this book is the one currently playing and the actual playback state
   const isCurrentlyPlaying = currentlyPlayingUuid === book.uuid;
   const isActuallyPlaying =
      isCurrentlyPlaying && audioContext?.playbackState?.isPlaying;

   // Check if the book is still generating
   const isGenerating = useMemo(() => {
      // If generation_status exists and is not 'completed' or similar values
      return (
         book.generation_status &&
         !["completed", "done", "finished", "ready"].includes(
            book.generation_status?.toLowerCase()
         )
      );
   }, [book.generation_status]);

   useEffect(() => {
      let isMounted = true;

      const loadImage = async () => {
         // Skip loading if we already have the image
         if (imageLoadedRef.current && imageSrc) {
            return;
         }

         try {
            let cdsToken = null;
            if (!book.is_public) {
               try {
                  cdsToken = await getCurrentCDSToken();
               } catch (err) {
                  console.error("Failed to get CDS token:", err);
                  throw new Error("Failed to authenticate with CDS");
               }
            }

            const imageUrl = `${config.cdsUrl}/user/audiobooks/${book.uuid}/cover?size=128x128&user_id=${userId}`;
            const headers = {
               Accept: "image/jpeg,image/png,*/*",
               Origin: config.primaryUrl,
            };

            if (cdsToken) {
               headers["Authorization"] = JSON.stringify(cdsToken);
            }

            const response = await fetch(imageUrl, {
               method: "GET",
               headers,
               credentials: "include",
               mode: "cors",
            });

            if (!response.ok) {
               throw new Error(`Failed to load image: ${response.status}`);
            }

            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);

            if (isMounted) {
               setImageSrc(objectUrl);
               setLoading(false);
               setError(false);
               imageLoadedRef.current = true; // Mark that we've loaded the image
            }
         } catch (err) {
            console.error("Error loading image:", err);
            if (isMounted) {
               setError(true);
               setLoading(false);
            }
         }
      };

      loadImage();

      return () => {
         isMounted = false;
         if (imageSrc) {
            URL.revokeObjectURL(imageSrc);
         }
      };
   }, [book.uuid, imageSrc, book.is_public, userId]);

   // Handle clicking the play/pause button
   const handlePlayPauseClick = () => {
      // Prevent playback if book is still generating
      if (isGenerating) {
         console.log("Book is still generating, playback not available");
         return;
      }

      if (isCurrentlyPlaying && audioContext && audioContext.togglePlayPause) {
         // If it's playing, just toggle to pause
         audioContext.togglePlayPause();
      } else {
         // Otherwise load and play the book
         onListen(book);
      }
   };

   // Last played date formatting
   const formatLastPlayed = (timestamp) => {
      if (!timestamp) return "Recently played";

      const now = new Date();
      const lastPlayed = new Date(timestamp);
      const diffTime = Math.abs(now - lastPlayed);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
         const hours = lastPlayed.getHours();
         const minutes = lastPlayed.getMinutes();
         const ampm = hours >= 12 ? "PM" : "AM";
         const formattedHours = hours % 12 === 0 ? 12 : hours % 12;
         const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
         return `Today at ${formattedHours}:${formattedMinutes} ${ampm}`;
      } else if (diffDays === 1) {
         return "Yesterday";
      } else if (diffDays < 7) {
         return `${diffDays} days ago`;
      } else {
         return `${lastPlayed.toLocaleDateString()}`;
      }
   };

   // Generate style for play button based on generation status
   const getPlayButtonStyle = () => {
      // Base style
      const baseStyle = styles.playButton(isCurrentlyPlaying);

      // Add disabled styling if book is generating
      if (isGenerating) {
         const isLight = theme === "light";
         return {
            ...baseStyle,
            opacity: 0.7,
            cursor: "default",
            backgroundColor: isLight ? "#f3f4f6" : "#2d3748",
            borderColor: isLight ? "#d1d5db" : "#4b5563",
            color: isLight ? "#9ca3af" : "#6b7280",
         };
      }

      return baseStyle;
   };

   return (
      <div style={styles.container(isCurrentlyPlaying)}>
         {/* Play/Pause button and image */}
         <div style={{ display: "flex", alignItems: "center" }}>
            <button
               onClick={handlePlayPauseClick}
               style={getPlayButtonStyle()}
               title={isGenerating ? "Generation in progress..." : "Play"}
               disabled={isGenerating}
            >
               {isGenerating ? (
                  // Show a spinner when generating
                  <div
                     style={{
                        width: "16px",
                        height: "16px",
                        borderRadius: "50%",
                        border: `2px solid ${
                           theme === "light" ? "#9ca3af" : "#6b7280"
                        }`,
                        borderTopColor: "transparent",
                        animation: "spin 1s linear infinite",
                     }}
                  />
               ) : isCurrentlyPlaying && isActuallyPlaying ? (
                  <span style={{ fontSize: "18px", lineHeight: 1 }}>❚❚</span>
               ) : (
                  <span>▶</span>
               )}
            </button>
            {loading ? (
               <div style={styles.imagePlaceholder}></div>
            ) : error ? (
               <div style={styles.imagePlaceholder}></div>
            ) : (
               <img
                  src={imageSrc || "/api/placeholder/56/56"}
                  alt={`${book.title || "Book"} cover`}
                  style={styles.bookImage}
               />
            )}

            {/* Title and chapter info */}
            <div style={styles.titleContainer}>
               <h3 style={styles.bookTitle(isCurrentlyPlaying)}>
                  {book.title || "Untitled Audiobook"}
               </h3>

               {/* Chapter info on its own line */}
               <p style={styles.chapterInfo}>
                  {`Chapter ${book.current_chapter || 1} of ${
                     book.num_total_chapters || 1
                  }`}
               </p>

               {/* Generation status indicator */}
               {isGenerating && (
                  <p
                     style={{
                        color: theme === "light" ? "#9ca3af" : "#d1d5db",
                        fontSize: "0.7rem",
                        fontStyle: "italic",
                        margin: "2px 0 0 0",
                     }}
                  >
                     Generation in progress...
                  </p>
               )}

               {/* Last played timestamp on a separate line */}
               <p style={styles.lastPlayed}>
                  Last Played: {formatLastPlayed(book.last_played)}
               </p>
            </div>
         </div>

         {/* Right side section with additional info if needed */}
         <div style={styles.rightSide}>
            {book.is_public && <div style={styles.publicBadge}>Public</div>}
         </div>

         {/* Add CSS for the spinner animation */}
         <style>
            {`
            @keyframes spin {
               0% { transform: rotate(0deg); }
               100% { transform: rotate(360deg); }
            }
            `}
         </style>
      </div>
   );
};

const ContinueListening = () => {
   const { theme } = useTheme(); // Get current theme
   const styles = getPageStyles(theme);

   const [recentlyPlayed, setRecentlyPlayed] = useState([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(null);
   const { isAuthenticated } = useUser();
   const token = localStorage.getItem("token");
   const isMountedRef = useRef(true);
   const initialDataFetchedRef = useRef(false);

   // Get the audio context for playing audiobooks
   const audioContext = useContext(AudioContext);
   const currentlyPlayingUuid = audioContext?.currentBook?.uuid || null;

   // Function to fetch recently played audiobooks
   const fetchRecentlyPlayed = async () => {
      if (!isMountedRef.current || !isAuthenticated) return;

      // Only fetch if this is the initial page load and data hasn't been fetched yet
      if (initialDataFetchedRef.current) {
         console.log(
            "Skipping fetch - data already loaded and no page reload detected"
         );
         return;
      }

      try {
         if (!token) {
            console.error("No authentication token found.");
            setError("Authentication token is missing. Please log in again.");
            setLoading(false);
            return;
         }

         setLoading(true);

         console.log("Fetching recently played audiobooks...");

         const res = await fetch("/api/user/listening-history", {
            headers: {
               Authorization: `Bearer ${token}`,
               "Cache-Control": "no-cache", // Add no-cache to prevent stale data
            },
         });

         if (!isMountedRef.current) return;

         if (res.ok) {
            const data = await res.json();
            console.log("Recently played API response:", data);

            // Handle both null and array responses as valid
            if (Array.isArray(data)) {
               setRecentlyPlayed(data);
               setError(null);
            } else if (data === null) {
               // Treat null as an empty array
               console.log("API returned null, treating as empty history");
               setRecentlyPlayed([]);
               setError(null); // No error, just an empty history
            } else {
               console.warn("API returned invalid format:", data);
               setRecentlyPlayed([]);
               setError(
                  "API response format error: Expected an array of recently played items"
               );
            }
         } else if (res.status === 404) {
            // Special treatment for 404 - interpret as "no history yet" rather than an error
            console.log("API returned 404 - User has no listening history yet");
            setRecentlyPlayed([]);
            setError(null); // No error, just an empty history
         } else {
            // Handle other API errors with appropriate status code
            console.error(`API error: ${res.status} - ${res.statusText}`);
            setRecentlyPlayed([]);
            setError(
               `Failed to fetch listening history: ${
                  res.statusText || "Server error"
               }`
            );
         }

         // Mark that initial data has been fetched
         initialDataFetchedRef.current = true;
      } catch (error) {
         console.error("Error fetching recently played:", error);
         setRecentlyPlayed([]);
         setError(
            `Error: ${error.message || "Failed to fetch listening history"}`
         );
      } finally {
         if (isMountedRef.current) {
            setLoading(false);
         }
      }
   };

   // Load data on component mount
   useEffect(() => {
      console.log("ContinueListening component mounted");
      isMountedRef.current = true;
      initialDataFetchedRef.current = false; // Reset the flag on mount

      // Initial fetch
      fetchRecentlyPlayed();

      // Refresh data ONLY when the page visibility changes from hidden to visible
      // (this typically happens when the user navigates back to the page)
      const handleVisibilityChange = () => {
         if (document.visibilityState === "visible") {
            console.log("Tab is visible - checking if page reload is needed");
            // Reset the flag to allow fetching data again when the page becomes visible
            initialDataFetchedRef.current = false;
            fetchRecentlyPlayed();
         }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      // Cleanup
      return () => {
         console.log("ContinueListening component unmounting");
         isMountedRef.current = false;
         document.removeEventListener(
            "visibilitychange",
            handleVisibilityChange
         );
      };
   }, [isAuthenticated]);

   // Enhanced function to handle listening to an audiobook with explicit progress saving
   const handleListen = async (book) => {
      if (!audioContext || !audioContext.loadBook) {
         console.error("Audio context or loadBook function not available");
         return;
      }

      console.log(
         `🎧 ContinueListening: Starting to play audiobook: ${book.title}`
      );

      // First, force save the current progress if we have a book playing
      if (audioContext.currentBook && audioContext.forceSaveProgress) {
         console.log(
            `📋 ContinueListening: Forcing save for current book before switching`
         );
         try {
            // Wait for force save to complete
            const saved = await audioContext.forceSaveProgress();
            console.log(
               `📋 ContinueListening: Force save result: ${
                  saved ? "Success" : "Failed"
               }`
            );
         } catch (err) {
            console.error(
               "📋 ContinueListening: Error during force save:",
               err
            );
         }
      } else {
         console.log(
            `📋 ContinueListening: No current book to save or forceSaveProgress not available`
         );
      }

      // Brief delay to ensure saving completes
      console.log(`📋 ContinueListening: Waiting before loading new book...`);
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Now load the new book
      console.log(`📋 ContinueListening: Now loading book: ${book.title}`);

      // Determine the chapter to start from
      const startChapter = book.current_chapter || 1;

      // Load the book with the saved position
      audioContext.loadBook(book, startChapter, true, book.current_position);

      // Note: Don't refetch data here - we want to maintain the original order
      // until the page is reloaded
   };

   // Method to handle when a book is selected for generation
   const handleBookSelected = (book) => {
      console.log(
         `Book selected for generation from recently played: ${book.title} (${book.uuid})`
      );

      // No need to navigate away anymore since AudiobookPromptSelector handles
      // the generation in place
   };

   // Show loading state
   if (loading) {
      return (
         <div style={styles.container}>
            <AudiobookPromptSelector onBookSelected={handleBookSelected} />

            <h2 className="inter-font" style={styles.heading}>
               Continue Listening
            </h2>

            <div style={styles.loadingContainer}>
               <div className="spinner-border text" role="status">
                  <span className="visually-hidden">Loading...</span>
               </div>
               <span style={styles.loadingText}>
                  Loading your listening history...
               </span>
            </div>
         </div>
      );
   }

   // Show error state if there's an error
   if (error) {
      return (
         <div style={styles.container}>
            <h2 className="inter-font" style={styles.heading}>
               Continue Listening
            </h2>
            <div style={styles.errorContainer}>
               <p style={styles.errorText}>
                  <strong>Error:</strong> {error}
               </p>
               <button
                  onClick={() => {
                     initialDataFetchedRef.current = false; // Reset the flag to allow fetch
                     fetchRecentlyPlayed();
                  }}
                  style={styles.errorButton}
               >
                  Try Again
               </button>
            </div>
         </div>
      );
   }

   return (
      <div style={styles.container}>
         <AudiobookPromptSelector onBookSelected={handleBookSelected} />

         <h2 className="inter-font" style={styles.heading}>
            Continue Listening
         </h2>

         {recentlyPlayed && recentlyPlayed.length > 0 ? (
            <div>
               {recentlyPlayed.map((item) => (
                  <RecentlyPlayedItem
                     key={item.uuid}
                     book={item}
                     onListen={handleListen}
                     currentlyPlayingUuid={currentlyPlayingUuid}
                     theme={theme} // Pass theme to item component
                  />
               ))}
            </div>
         ) : (
            <div style={styles.emptyStateContainer}>
               <div style={styles.emptyStateIcon}>
                  <span style={styles.emptyStateIconText}>🎧</span>
               </div>
               <p style={styles.emptyStateHeading}>
                  Your listening history is empty
               </p>
               <p style={styles.emptyStateText}>
                  Start listening to audiobooks and they'll appear here so you
                  can easily continue where you left off.
               </p>
               <div style={styles.buttonContainer}>
                  <button
                     onClick={() => window.setView && window.setView("public")}
                     style={styles.primaryButton}
                     onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor =
                           styles.primaryButtonHover.backgroundColor;
                     }}
                     onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor =
                           theme === "light"
                              ? "var(--accent-primary)"
                              : "#10b981";
                     }}
                  >
                     Discover Stories
                  </button>
                  <button
                     onClick={() => window.setView && window.setView("library")}
                     style={styles.secondaryButton}
                     onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor =
                           styles.secondaryButtonHover.backgroundColor;
                        e.currentTarget.style.borderColor =
                           styles.secondaryButtonHover.borderColor;
                     }}
                     onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.borderColor =
                           theme === "light"
                              ? "var(--border-color)"
                              : "rgba(255, 255, 255, 0.2)";
                     }}
                  >
                     My Library
                  </button>
               </div>
            </div>
         )}
      </div>
   );
};

export default ContinueListening;
